// Player actions for the rotary era (M2_SPEC §1–§6, §11, §13). core/actions.js re-exports thin wrappers.
// Every function returns { ok, msg } and mutates state.
import { eqDef, stat, rigStats, unitPrice, yf } from './defs.js';
import { wellRig, setTask, ACTIVE_STATUSES } from './drilling.js';
import { RPM_MIN, RPM_MAX, contractDrillImpl, recompleteImpl, workPipeImpl, fishImpl, sidetrackImpl, isRotaryRig } from './rotary.js';
import { mudDef, mudRange, mudAvailable, pressureWindow } from './mud.js';
import { weightUp, resolveGusherImpl } from './wellcontrol.js';
import { cementCasingImpl } from './cement.js';
import { inspectPipeImpl } from './pipe.js';
import { assignToolpusherImpl, setDrillingProgramImpl, programTemplate as tpl, toolpusherFor, hasMudMan, rigUpMult } from './automation.js';
import { rule37Fee, skidDays, adjacent } from './fieldops.js';
import { MUD } from '../data/m2/rules.js';
import { clamp, spend, fmt$ } from './util.js';

const ok = (msg, extra = {}) => ({ ok: true, msg, ...extra });
const no = (msg) => ({ ok: false, msg });
const getWell = (state, id) => state.wells[id] ?? null;

// ---- shop gates & mounting -------------------------------------------------------------------
// M2_SPEC §18.6: a rotary rig researched before 1901 brings its consumables (pipe, mud, fishtail bits, pump) with it.
export function rotaryKitUnlocked(state, def) {
  if (!def || yf(state) >= def.year || def.year > 1902) return false;
  if (!['pipe', 'mud', 'bit', 'rig_gear'].includes(def.cat) || !(def.rigTypes ?? []).includes('rotary')) return false;
  return Object.values(state.rigs).some((r) => isRotaryRig(r));
}

const slotOf = (def) => (def?.cat === 'rig_gear' ? stat(def, 'slot', null) : def?.cat === 'bop' ? (stat(def, 'cappingOnly', false) ? 'capping' : 'bop') : null);

function mount(state, rig, it) {
  const def = eqDef(it.defId);
  const slot = slotOf(def);
  if (!slot) return false;
  rig.gear ??= { pump: null, collars: null, capping: null };
  const cur = slot === 'bop' ? rig.bopUid : rig.gear[slot];
  if (cur && state.items[cur]) state.items[cur].loc = { type: 'warehouse', id: null };
  it.loc = { type: 'rig', id: rig.uid };
  if (slot === 'bop') rig.bopUid = it.uid; else rig.gear[slot] = it.uid;
  return true;
}

export function autoMount(state, it, rigUid = null) {
  const def = eqDef(it.defId);
  const slot = slotOf(def);
  if (!slot) return false;
  const free = (r) => isRotaryRig(r) && !(slot === 'bop' ? r.bopUid && state.items[r.bopUid] : r.gear?.[slot] && state.items[r.gear[slot]]);
  const rig = rigUid ? state.rigs[rigUid] : Object.values(state.rigs).find(free);
  if (!rig || (!rigUid && !free(rig))) return false;
  if (def.rigTypes && !def.rigTypes.includes(rigStats(eqDef(rig.defId)).rigType)) return false;
  return mount(state, rig, it);
}

export function autoEquipRotary(state, rig) {
  if (!isRotaryRig(rig)) return;
  for (const it of Object.values(state.items)) {
    if (it.loc.type !== 'warehouse') continue;
    const slot = slotOf(eqDef(it.defId));
    if (!slot) continue;
    const taken = slot === 'bop' ? rig.bopUid && state.items[rig.bopUid] : rig.gear?.[slot] && state.items[rig.gear[slot]];
    if (!taken) mount(state, rig, it);
  }
}

export function unmountAll(state, rig) {
  for (const uid of [rig.bopUid, ...Object.values(rig.gear ?? {})]) if (uid && state.items[uid]) state.items[uid].loc = { type: 'warehouse', id: null };
  rig.bopUid = null;
  rig.gear = { pump: null, collars: null, capping: null };
}

export function installGear(state, rigUid, itemUid) {
  const rig = state.rigs[rigUid];
  const it = state.items[itemUid];
  if (!rig || !it) return no('Invalid rig or item.');
  const def = eqDef(it.defId);
  if (!slotOf(def)) return no(`${def?.name ?? 'That'} is not rig gear.`);
  if (it.loc.type !== 'warehouse') return no('That item is in use.');
  if (def.rigTypes && !def.rigTypes.includes(rigStats(eqDef(rig.defId)).rigType)) return no(`${def.name} doesn't fit a ${rigStats(eqDef(rig.defId)).rigType} rig.`);
  mount(state, rig, it);
  return ok(`${def.name} installed on ${rig.name}.`);
}

export function removeGear(state, rigUid, slot) {
  const rig = state.rigs[rigUid];
  if (!rig) return no('No such rig.');
  const uid = slot === 'bop' ? rig.bopUid : rig.gear?.[slot];
  if (!uid || !state.items[uid]) return no('Nothing in that slot.');
  state.items[uid].loc = { type: 'warehouse', id: null };
  if (slot === 'bop') rig.bopUid = null; else rig.gear[slot] = null;
  return ok(`${eqDef(state.items[uid].defId).name} back in the warehouse.`);
}

// Pressure-test the BOP: 5% of its price, restores it to 100% (its fail chance rises as condition falls).
export function testBop(state, rigUid) {
  const rig = state.rigs[rigUid];
  const it = rig?.bopUid ? state.items[rig.bopUid] : null;
  if (!it) return no('No blowout preventer on that rig.');
  const cost = unitPrice(state, eqDef(it.defId)) * 0.05;
  if (!spend(state, cost, 'other')) return no(`A BOP test costs ${fmt$(cost)}.`);
  it.condition = 100;
  it.lastTest = state.day;
  return ok(`${eqDef(it.defId).name} tested and serviced (${fmt$(cost)}).`);
}

// ---- RPM & mud -------------------------------------------------------------------------------
const rotaryWell = (w) => w && (w.rigType === 'rotary' || w.contract);

export function setRpm(state, wellId, x) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  if (!rotaryWell(w)) return no('RPM is a rotary-rig setting.');
  w.rpm = Math.round(clamp(Number(x) || 1, RPM_MIN, RPM_MAX) * 100) / 100;
  const label = w.rpm < 0.85 ? 'Slow' : w.rpm < 1.15 ? 'Normal' : w.rpm < 1.45 ? 'Fast' : 'Spin it';
  return ok(`${w.name}: ${label} (${w.rpm.toFixed(2)}).`);
}

export function setMudType(state, wellId, defId) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  if (!rotaryWell(w)) return no('Cable-tool holes are bailed, not mudded.');
  const def = eqDef(defId);
  if (!def || def.cat !== 'mud') return no('Not a drilling mud.');
  if (!mudAvailable(state, def)) return no(`${def.name} is not available until ${def.year}.`);
  w.mud.defId = def.id;
  const [lo, hi] = mudRange(def);
  if (typeof w.mud.ppg === 'number') w.mud.ppg = clamp(w.mud.ppg, lo, hi);
  return ok(`${w.name}: switched to ${def.name} (${lo}–${hi} ppg).`);
}

export function setMudWeight(state, wellId, ppg) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  if (!rotaryWell(w)) return no('Cable-tool holes are bailed, not mudded.');
  const rig = wellRig(state, w);
  if (ppg === 'auto') {
    if (!rig || !(toolpusherFor(state, rig) || hasMudMan(state, rig))) return no('Auto mud weight needs a mud man on the rig or a toolpusher supervising it.');
    w.mud.auto = true;
    if (rig) rig.auto.mudAuto = true;
    return ok(`${w.name}: ${hasMudMan(state, rig) ? 'the mud man' : 'the toolpusher'} is watching the weight.`);
  }
  const def = mudDef(w);
  const [lo, hi] = def ? mudRange(def) : [8.3, 8.8];
  const v = Number(ppg);
  if (!Number.isFinite(v)) return no('Mud weight must be a number of ppg.');
  w.mud.auto = false;
  w.mud.ppg = Math.round(clamp(v, lo, hi) * 10) / 10;
  const capped = v > hi + 1e-9 ? ` (${def?.name ?? 'this mud'} holds at most ${hi} ppg)` : v < lo - 1e-9 ? ` (minimum ${lo})` : '';
  return ok(`${w.name}: mud weight ${w.mud.ppg.toFixed(1)} ppg${capped}.`);
}

export function setMud(state, wellId, defId, ppg) {
  if (defId) { const r = setMudType(state, wellId, defId); if (!r.ok) return r; }
  if (ppg != null) return setMudWeight(state, wellId, ppg);
  return ok('Mud set.');
}

// Weight the open hole up to kill weight (max open pore + MUD.killMarginPpg) right now, paying for the barite.
export function killWell(state, wellId) {
  const w = getWell(state, wellId);
  if (!w || !rotaryWell(w)) return no('Only rotary holes are killed with mud.');
  if (!['DRILLING', 'PAUSED', 'AT_TD', 'TRIPPING', 'DRY'].includes(w.status)) return no(`Can't do that while ${w.status}.`);
  const win = pressureWindow(state, w);
  const target = Math.round((win.pore + MUD.killMarginPpg) * 10) / 10;
  if (target <= (w.mud.ppg ?? 0) + 1e-9) return no(`Already at ${Number(w.mud.ppg).toFixed(1)} ppg — above kill weight (${target.toFixed(1)}).`);
  const full = weightUp(state, w, target);
  w.mud.auto = false;
  return ok(`${w.name}: weighted up to ${Number(w.mud.ppg).toFixed(1)} ppg${full ? '' : ` (the most this mud holds; kill weight is ${target.toFixed(1)})`}.${w.mud.ppg + MUD.ecdPerKft * w.depth / 1000 > win.frac ? ' Careful: that is above the frac gradient.' : ''}`);
}

// ---- casing, stuck pipe, pipe rack -----------------------------------------------------------
export function cementCasing(state, wellId, cementDefId) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  return cementCasingImpl(state, w, cementDefId ?? null, setTask);
}

export const workPipe = (state, wellId) => (getWell(state, wellId) ? workPipeImpl(state, getWell(state, wellId)) : no('No such well.'));
export function fish(state, wellId) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  if (w.status !== 'STUCK') return no(w.status === 'FISHING' ? 'Already fishing.' : 'Nothing is stuck.');
  return fishImpl(state, w);
}
export function sidetrack(state, wellId) {
  const w = getWell(state, wellId);
  if (!w || !rotaryWell(w)) return no('Sidetracking a stuck string is a rotary job (cable holes: straighten).');
  if (!['STUCK', 'FISHING'].includes(w.status)) return no('Only a stuck or junked hole needs a sidetrack.');
  return sidetrackImpl(state, w);
}
export const inspectPipe = (state, pipeId) => inspectPipeImpl(state, pipeId);

// ---- contract drilling, recompletion ------------------------------------------------------------
export function contractDrill(state, fieldId, plotIdx, targetFt) {
  const fee = rule37Fee(state, fieldId, plotIdx);
  if (fee > 0 && state.company.cash < fee) return no(`A Rule 37 exception permit costs ${fmt$(fee)}.`);
  const r = contractDrillImpl(state, fieldId, plotIdx, targetFt);
  if (r.ok && fee > 0) { spend(state, fee, 'other', false); r.msg += ` Rule 37 permit ${fmt$(fee)}.`; }
  return r;
}
export function recomplete(state, wellId, resId) {
  const w = getWell(state, wellId);
  if (!w) return no('No such well.');
  return recompleteImpl(state, w, resId);
}

// ---- toolpusher & programs ------------------------------------------------------------------------
export const assignToolpusher = (state, crewId, rigUids) => assignToolpusherImpl(state, crewId, rigUids);
export const setDrillingProgram = (state, rigUid, program) => setDrillingProgramImpl(state, rigUid, program);
export const programTemplate = (state, fieldId) => tpl(state, fieldId);

// ---- skidding a rig to the next lot (M2_SPEC §13) --------------------------------------------------
export function checkMoveRig(state, rigUid, plotIdx) {
  const rig = state.rigs[rigUid];
  if (!rig) return no('No such rig.');
  if (!rig.at) return no(`${rig.name} is in the yard — assign it to a plot instead.`);
  const cur = rig.wellId ? state.wells[rig.wellId] : null;
  if (cur && ACTIVE_STATUSES.has(cur.status) && cur.status !== 'PAUSED') return no(`${rig.name} is busy at ${cur.name}.`);
  const f = state.fields[rig.at.fieldId];
  if (!adjacent(f, rig.at.plot, plotIdx)) return no('Skidding only reaches the neighbouring lots.');
  const p = f.plots[plotIdx];
  if (!p?.owned) return no('Lease the plot first.');
  return { ok: true, fieldId: rig.at.fieldId, target: cur?.target ?? null };
}

export function afterMoveRig(state, rigUid, r) {
  const rig = state.rigs[rigUid];
  const t = rig?.task?.timerId ? state.timers[rig.task.timerId] : null;
  if (t && t.kind === 'RIG_UP') {
    const d = Math.max(1, Math.ceil(skidDays(rig) * rigUpMult(state, rig) - 1e-9));
    t.daysLeft = t.totalDays = d;
    rig.task.daysLeft = d;
    return ok(`${rig.name} skidding to the next lot (${d} day${d > 1 ? 's' : ''}).`);
  }
  return r;
}

export const resolveGusher = (state, decisionId, choice) => resolveGusherImpl(state, decisionId, choice);

export { rule37Fee };
